import React, { useRef } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { Button, Menu, MenuItem, Link } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEllipsisH } from '@fortawesome/pro-solid-svg-icons'
import { format, parseISO } from 'date-fns'

import permissions from 'common/system-permissions'
import usePermissions from 'common/use-permissions'
import features from 'common/college-features'
import { isFeatureEnabled } from 'store/college'
import useCalendarTransition from 'common/use-calendar-transition'
import { hasPermission } from 'store/user'
import { useOpenModalForCreate } from 'components/ComposeOutreachMessageButton/Context'
import { getDefaultMessage } from 'components/ComposeOutreachMessageButton/services/message-service'
import { ONE_TIME_TRIGGER } from 'components/SegmentSelector/outreach-message-trigger-constants'
import { setHighSchools } from 'components/SegmentSelector/segment-service'

const useStyles = makeStyles({
  scheduleVisitLink: {
    color: 'inherit',
    textDecoration: 'none',

    '&:hover': {
      color: 'inherit',
      textDecoration: 'none',
    },
  },
})

const Actions = ({ open, handleOpen, handleClose, openAccessModal, openContactCounselorModal, school }) => {
  const canPerformVisitManagementTasks = usePermissions(permissions.PERFORM_VISIT_MANGEMENT_TASKS)
  const outreachEnabled = useSelector((store) => isFeatureEnabled(store, features.SUPPORTS_STUDENT_OUTREACH))
  const allowedToMessageStudents = useSelector((store) =>
    hasPermission(store, permissions.MANAGE_COLLEGE_OUTREACH_MESSAGES),
  )
  const canMessageStudents = outreachEnabled && allowedToMessageStudents
  const openCreateOutreachMessageModal = useOpenModalForCreate()
  const canScheduleVisits = school.next_visit_availability && canPerformVisitManagementTasks

  const handleMessageCounselorAction = () => {
    if (canPerformVisitManagementTasks) {
      openContactCounselorModal(school)
    } else {
      openAccessModal({ role: permissions.PERFORM_VISIT_MANGEMENT_TASKS })
    }
    handleClose()
  }

  const handleMessageStudentsAction = () => {
    if (canMessageStudents) {
      const message = getDefaultMessage(ONE_TIME_TRIGGER)
      message.Segment = setHighSchools(message.Segment, [{ id: school.id, name: school.name }])
      openCreateOutreachMessageModal(message)
    } else {
      const access = outreachEnabled
        ? { role: permissions.MANAGE_COLLEGE_OUTREACH_MESSAGES }
        : { feature: features.SUPPORTS_STUDENT_OUTREACH }
      openAccessModal(access)
    }
    handleClose()
  }

  const handleTransitionToCalendar = useCalendarTransition(school.id, school.name)

  const handleScheduleVisitAction = () => {
    handleTransitionToCalendar()
    handleClose()
  }

  const actionsButton = useRef(null)
  const styles = useStyles()

  return (
    <React.Fragment>
      <Button
        id={`actions-menu-btn-${school.id}`}
        ref={actionsButton}
        onClick={() => handleOpen(school)}
      >
        <FontAwesomeIcon icon={faEllipsisH} />
      </Button>
      <Menu
        open={open}
        onClose={handleClose}
        anchorEl={actionsButton.current}
        anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
        getContentAnchorEl={null}
      >
        {canScheduleVisits ? (
          <Link
            href={
              canScheduleVisits &&
              `${import.meta.env.BASE_URL}/recruit/visits/view/week?focus=${format(
                parseISO(school.next_visit_availability),
                'yyyyMMdd',
              )}`
            }
            className={styles.scheduleVisitLink}
          >
            <MenuItem onClick={handleScheduleVisitAction}>Schedule Visit</MenuItem>
          </Link>
        ) : (
          <MenuItem disabled={true}>Schedule Visit</MenuItem>
        )}
        <MenuItem onClick={handleMessageStudentsAction}>Message Students</MenuItem>
        <MenuItem onClick={handleMessageCounselorAction}>Message Counselor</MenuItem>
      </Menu>
    </React.Fragment>
  )
}

Actions.propTypes = {
  open: PropTypes.bool.isRequired,
  handleOpen: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  openContactCounselorModal: PropTypes.func.isRequired,
  openAccessModal: PropTypes.func.isRequired,
  school: PropTypes.object.isRequired,
}

export default Actions
